import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CTASection = () => {
  const ctaOptions = [
    {
      id: 1,
      title: "Start Your Project",
      subtitle: "Ready to Build Something Amazing?",
      description: "Let's collaborate on your next web or mobile application. From concept to deployment, I'll help bring your vision to life with cutting-edge technology.",
      icon: "Rocket",
      primaryAction: {
        text: "Get Free Consultation",
        link: "/contactpage",
        variant: "default"
      },
      secondaryAction: {
        text: "View Portfolio",
        link: "/contactpage",
        variant: "outline"
      },
      benefits: [
        "Free initial consultation",
        "Custom solution design",
        "Agile development process",
        "Post-launch support"
      ],
      gradient: "from-blue-500/10 to-cyan-500/10",
      borderColor: "border-blue-500/20"
    },
    {
      id: 2,
      title: "Join Our Community",
      subtitle: "Connect with 15,000+ Developers",
      description: "Become part of CodersHub - a thriving global community where developers learn, collaborate, and grow together. Access exclusive resources and mentorship opportunities.",
      icon: "Users",
      primaryAction: {
        text: "Join CodersHub",
        link: "/aboutpage",
        variant: "default"
      },
      secondaryAction: {
        text: "Learn More",
        link: "/aboutpage",
        variant: "outline"
      },
      benefits: [
        "Global developer network",
        "Exclusive learning resources",
        "Mentorship opportunities",
        "Career growth support"
      ],
      gradient: "from-emerald-500/10 to-teal-500/10",
      borderColor: "border-emerald-500/20"
    },
    {
      id: 3,
      title: "Get Mentorship",
      subtitle: "Accelerate Your Developer Journey",
      description: "Join the IMAC program and get personalized mentorship to level up your skills, advance your career, and build confidence in your coding journey.",
      icon: "GraduationCap",
      primaryAction: {
        text: "Apply for Mentorship",
        link: "/contactpage",
        variant: "default"
      },
      secondaryAction: {
        text: "Success Stories",
        link: "/contactpage",
        variant: "outline"
      },
      benefits: [
        "1-on-1 mentorship sessions",
        "Personalized learning path",
        "Career guidance",
        "Industry connections"
      ],
      gradient: "from-purple-500/10 to-pink-500/10",
      borderColor: "border-purple-500/20"
    }
  ];

  const quickStats = [
    { label: "Projects Delivered", value: "5+", icon: "CheckCircle" },
    { label: "Happy Clients", value: "5+", icon: "Heart" },
    { label: "Community Members", value: "1K+", icon: "Users" },
    { label: "Success Stories", value: "100+", icon: "Trophy" }
  ];

  return (
    <section className="py-20 bg-gradient-brand relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-10 w-64 h-64 bg-primary/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-accent/5 rounded-full blur-3xl"></div>
      </div>
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <Icon name="Zap" size={20} className="text-primary" />
            <span className="text-sm font-brand-medium text-primary tracking-wide uppercase">Take Action</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-brand-headline text-white mb-6">
            Ready to Start Your
            <span className="block text-primary text-gradient-gold">Growth Journey?</span>
          </h2>
          <p className="text-lg text-gray-300 max-w-3xl mx-auto font-brand-body">
            Whether you need a technical partner, want to join our community, or seek mentorship, 
            I'm here to help you achieve your goals and unlock your potential.
          </p>
        </div>

        {/* CTA Options Grid */}
        <div className="grid lg:grid-cols-3 gap-8 mb-16">
          {ctaOptions?.map((option) => (
            <div
              key={option?.id}
              className={`bg-background/95 backdrop-blur-sm rounded-2xl p-8 border ${option?.borderColor} hover:border-primary/50 transition-all duration-500 hover:scale-105 brand-shadow-lg group`}
            >
              {/* Background Gradient */}
              <div className={`absolute inset-0 bg-gradient-to-br ${option?.gradient} rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500`}></div>
              
              {/* Content */}
              <div className="relative z-10">
                {/* Icon */}
                <div className="mb-6">
                  <div className="w-16 h-16 bg-primary/10 rounded-xl flex items-center justify-center border border-primary/20">
                    <Icon name={option?.icon} size={32} className="text-primary" />
                  </div>
                </div>

                {/* Header */}
                <div className="mb-6">
                  <h3 className="text-xl font-brand-subheading text-foreground mb-2">
                    {option?.title}
                  </h3>
                  <p className="text-sm text-primary font-brand-medium mb-4">
                    {option?.subtitle}
                  </p>
                  <p className="text-text-secondary font-brand-body leading-relaxed">
                    {option?.description}
                  </p>
                </div>

                {/* Benefits */}
                <div className="mb-8">
                  <ul className="space-y-2">
                    {option?.benefits?.map((benefit, idx) => (
                      <li key={idx} className="text-sm text-text-secondary flex items-start">
                        <Icon name="Check" size={16} className="text-success mr-2 mt-0.5 flex-shrink-0" />
                        {benefit}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Actions */}
                <div className="space-y-3">
                  <Link to={option?.primaryAction?.link}>
                    <Button
                      variant={option?.primaryAction?.variant}
                      fullWidth
                      iconName="ArrowRight"
                      iconPosition="right"
                      iconSize={16}
                      className="bg-primary text-primary-foreground hover:bg-primary/90 brand-shadow"
                    >
                      {option?.primaryAction?.text}
                    </Button>
                  </Link>
                  <Link to={option?.secondaryAction?.link}>
                    <Button
                      variant={option?.secondaryAction?.variant}
                      fullWidth
                      iconName="ExternalLink"
                      iconPosition="right"
                      iconSize={16}
                      className="border-primary/30 text-primary hover:bg-primary/10"
                    >
                      {option?.secondaryAction?.text}
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
          {quickStats?.map((stat, idx) => (
            <div
              key={idx}
              className="text-center bg-background/10 backdrop-blur-sm rounded-xl p-6 border border-primary/20"
            >
              <Icon name={stat?.icon} size={32} className="text-primary mx-auto mb-3" />
              <div className="text-2xl font-brand-headline text-white mb-1">
                {stat?.value}
              </div>
              <div className="text-sm text-gray-300 font-brand-body">
                {stat?.label}
              </div>
            </div>
          ))}
        </div>

        {/* Contact Information */}
        <div className="text-center bg-background/10 backdrop-blur-sm rounded-2xl p-8 border border-primary/20">
          <h3 className="text-2xl font-brand-subheading text-white mb-4">
            Have Questions? Let's Talk!
          </h3>
          <p className="text-gray-300 font-brand-body mb-6 max-w-2xl mx-auto">
            I'm always excited to discuss new opportunities, answer questions about my work, 
            or chat about the latest in web development and community building.
          </p>
          
          {/* Contact Methods */}
          <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-8 mb-8">
            <a
              href="mailto:devcloudead0@gmail.com"
              className="flex items-center space-x-2 text-primary hover:text-primary/80 transition-colors"
            >
              <Icon name="Mail" size={20} />
              <span className="font-brand-medium">adrian@example.com</span>
            </a>
            <a
              href="https://www.linkedin.com/in/ebesoh-adrian"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center space-x-2 text-primary hover:text-primary/80 transition-colors"
            >
              <Icon name="Linkedin" size={20} />
              <span className="font-brand-medium">LinkedIn</span>
            </a>
            <a
              href="https://x.com/adrianebesoh237"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center space-x-2 text-primary hover:text-primary/80 transition-colors"
            >
              <Icon name="Twitter" size={20} />
              <span className="font-brand-medium">@adrian</span>
            </a>
          </div>

          {/* Response Time */}
          <div className="inline-flex items-center space-x-2 bg-success/20 text-success px-4 py-2 rounded-full border border-success/30">
            <Icon name="Clock" size={16} />
            <span className="text-sm font-brand-medium">Usually responds within 24 hours</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CTASection;